/**
 * Central HTTP layer for Inventory APIs.
 * Uses project axios (Axios-interceptor) — auth, org headers, 401 refresh.
 */
import axios from '../../login/Axios-interceptor';
import { BASE_URL } from '../constants';

const REQUEST_TIMEOUT_MS = 15000;

function sessionValue(key) {
  const value = sessionStorage.getItem(key);
  return value && value !== 'null' && value !== 'undefined' ? value : '';
}

export function getInventoryHeaders(includeJson = false) {
  const headers = {
    Authorization: `Bearer ${sessionValue('accessToken')}`,
    'APP-MODE': sessionValue('APP_MODE') || sessionStorage.getItem('appMode') || '',
    'ORG-CODE': sessionValue('ORG_CODE') || sessionStorage.getItem('orgCode') || '',
    'USER-ACCESS-KEY': sessionValue('USER_ACCESS_KEY') || sessionStorage.getItem('accessKey') || '',
    USERNAME: sessionValue('userName') || sessionStorage.getItem('username') || '',
  };
  if (includeJson) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

export function buildInventoryUrl(path) {
  const root = (BASE_URL || '').replace(/\/$/, '');
  let suffix = path.startsWith('/') ? path : `/${path}`;
  // Trailing slash is mandatory for pharmacy (and most inventory) Django routes.
  const [pathname, query = ''] = suffix.split('?');
  const withSlash = pathname.endsWith('/') ? pathname : `${pathname}/`;
  suffix = query ? `${withSlash}?${query}` : withSlash;
  return `${root}${suffix}`;
}

/** v1 routes live at {origin}/api/v1/... (not under /app/api). */
export function buildApiV1Url(path) {
  const base = (BASE_URL || '').replace(/\/$/, '');
  const origin = base.replace(/\/app\/api$/i, '') || base;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${origin}/api/v1${suffix}`;
}

function inventoryErrorCode(data) {
  if (!data || typeof data !== 'object') return undefined;
  if (data.detail?.error_code) return data.detail.error_code;
  return data.error?.code;
}

function inventoryErrorMessage(data) {
  if (!data || typeof data !== 'object') return 'Request failed';
  // Plain FastAPI-style errors: { "detail": "..." } (not wrapped)
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.detail === 'object' && data.detail?.message) return data.detail.message;
  return data.error?.detail || data.message || 'Request failed';
}

function assertInventorySuccess(data) {
  if (!data) return data;
  if (data.success === false) {
    const error = new Error(inventoryErrorMessage(data));
    error.code = inventoryErrorCode(data);
    error.apiResponse = data;
    throw error;
  }
  if (data.status === 'Failed') {
    const error = new Error(inventoryErrorMessage(data));
    error.code = inventoryErrorCode(data);
    error.apiResponse = data;
    throw error;
  }
  return data;
}

export function unwrapInventoryPayload(data) {
  const payload = assertInventorySuccess(data);
  if (!payload || typeof payload !== 'object') return payload;

  // `status` is intentionally kept: the EMR gateway merges the upstream resource
  // on top of its own envelope, so when the resource has its own `status` field
  // (GRN, PO, ...) that value overwrites the envelope's "Success" marker before
  // the JSON is ever sent — the wire response has exactly one `status` key,
  // holding the resource's real lifecycle status. Stripping it here would
  // silently delete that value from every response.
  const { is_valid, message, ...rest } = payload;
  if (Object.prototype.hasOwnProperty.call(rest, 'data') && rest.data !== undefined) {
    return rest.data;
  }
  return rest;
}

export function toInventoryList(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  return payload.items ?? payload.data ?? [];
}

export function toInventoryCount(payload) {
  if (Array.isArray(payload)) return payload.length;
  if (!payload || typeof payload !== 'object') return 0;
  if (typeof payload.total === 'number') return payload.total;
  return toInventoryList(payload).length;
}

/** Encode query params with encodeURIComponent (spaces as %20, not +). */
export function encodeInventoryQueryParams(params = {}) {
  return Object.entries(params)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');
}

function normalizeRequestError(err) {
  if (err?.response?.status === 401) {
    return err;
  }
  const body = err?.response?.data;
  const message = inventoryErrorMessage(body) || err?.message || 'Network error';
  const error = err instanceof Error ? err : new Error(message);
  error.message = message;
  error.code = inventoryErrorCode(body);
  error.status = err?.response?.status;
  if (body) {
    error.apiResponse = body;
  }
  return error;
}

async function inventoryRequest(method, path, { body, params, paramsSerializer, urlBuilder = buildInventoryUrl } = {}) {
  try {
    const response = await axios({
      method,
      url: urlBuilder(path),
      data: body,
      params,
      paramsSerializer,
      timeout: REQUEST_TIMEOUT_MS,
      headers: getInventoryHeaders(body != null),
    });
    return unwrapInventoryPayload(response.data);
  } catch (err) {
    throw normalizeRequestError(err);
  }
}

const client = {
  get: (path, params, options = {}) => inventoryRequest('GET', path, { params, ...options }),
  post: (path, body, params) => inventoryRequest('POST', path, { body, params }),
  put: (path, body, params) => inventoryRequest('PUT', path, { body, params }),
  patch: (path, body, params) => inventoryRequest('PATCH', path, { body, params }),
  delete: (path, params) => inventoryRequest('DELETE', path, { params }),
  postV1: (path, body, params) => inventoryRequest('POST', path, { body, params, urlBuilder: buildApiV1Url }),
};

export default client;

// API: GET /inventory/items/ — connectivity health check
export async function healthCheck() {
  try {
    const response = await axios.get(buildInventoryUrl('/inventory/items/'), {
      timeout: 3000,
      headers: getInventoryHeaders(),
    });
    return response.status < 500;
  } catch (err) {
    return err.response?.status === 401 || err.response?.status === 403;
  }
}
